import React, {Fragment, useContext} from 'react';

import b_ from 'b_';
import times from 'lodash/times';
import InfiniteScroll from 'react-infinite-scroller';
import useWindowSize from 'react-use/lib/useWindowSize';

import {AudienceType, SurveyType} from 'client/common/types';

import {ListMode, isSurveyItem} from 'client/common/store/reducers/surveys/types';

import {useDevice} from 'client/common/hooks/device';
import {useIsSuper} from 'client/common/hooks/user';

import ListModeContext from 'client/common/contexts/list-mode';

import SurveyItemMenu from 'client/quicksurvey/list/components/survey-item-menu';
import SurveyItemButton from 'client/quicksurvey/list/components/survey-item-button';
import SurveyState from 'client/common/components/survey-state';

import {Device} from 'client/common/utils/device-width';
import {AudienceTargeting} from 'client/common/utils/audience';
import {formatDate, formatNumber, formatPriceValue} from 'client/common/utils/format';
import {genSurveyLink} from 'client/common/utils/survey-link';
import {getIsFree} from 'client/common/utils/price';

import I18N from 'assets/i18n/brandlift-project';

import {ListComponentProps, ItemProps} from './types';

import './survey-list.css';

const i18n = I18N.list.surveyList;
const b = b_.with('survey-list');

const SKELETON_COUNT = 6;

function RowLoader() {
    return (
        <Fragment>
            {times(SKELETON_COUNT, index => (
                <SurveyRowSkeleton key={`skeleton-${index}`} />
            ))}
        </Fragment>
    );
}

function HeaderRow() {
    const listMode = useContext(ListModeContext);

    return (
        <div className={b('row', {type: 'header'})}>
            <div className={b('col', {type: 'name'})}>{i18n.tableHeader.name}</div>
            <div className={b('col', {type: 'date'})}>{i18n.tableHeader.date}</div>
            <div className={b('col', {type: 'state'})}>{i18n.tableHeader.state}</div>
            <div className={b('col', {type: 'answers'})}>{i18n.tableHeader.answers}</div>
            <div className={b('col', {type: 'cost'})}>{i18n.tableHeader.cost}</div>
            <div className={b('col', {type: 'targeting'})}>{i18n.tableHeader.targeting}</div>
            {listMode === ListMode.Agency && (
                <div className={b('col', {type: 'client'})}>{i18n.tableHeader.client}</div>
            )}
            <div className={b('col', {type: 'action'})} />
        </div>
    );
}

const SURVEY_TYPES_WITHOUT_FINISHES: Set<SurveyType> = new Set(['createSegment']);

export function SurveyRow(props: ItemProps) {
    const {categories, interests, segments, survey, isSuper} = props;

    const device = useDevice();
    const listMode = useContext(ListModeContext);

    if (survey.isLoading || !isSurveyItem(survey)) {
        return <SurveyRowSkeleton />;
    }

    const {
        audience,
        clientName,
        cost,
        name,
        slug,
        state,
        surveyType,
        updatedAt,
        paymentStatus,
        moderationVerdict
    } = survey;

    const isFree = getIsFree(cost);
    const {value_with_vat: priceWithVat, value_without_vat: priceWithoutVat, currency} = cost;
    const targeting = AudienceTargeting.formatAsString({
        request: audience,
        categories,
        interests,
        segments
    });

    return (
        <div className={b('row', {type: 'survey'})}>
            <a
                href={genSurveyLink({slug, surveyType})}
                className={b('col', {type: 'name'})}
                title={name}
            >
                {name}
            </a>
            <div className={b('col', {type: 'date'})}>
                {formatDate(updatedAt, device !== Device.Widescreen)}
            </div>
            <SurveyState
                className={b('col', {type: 'state'})}
                type='text'
                state={state}
                verdict={moderationVerdict}
                isSuper={isSuper}
                paymentStatus={paymentStatus}
            />
            <div className={b('col', {type: 'answers'})}>
                {SURVEY_TYPES_WITHOUT_FINISHES.has(surveyType) ? (
                    <span className={b('finished-answers')}>{i18n.answers.empty}</span>
                ) : (
                    <>
                        <span className={b('finished-answers')}>
                            {i18n.answers.finished.replace('%s', formatNumber(audience.finished))}
                        </span>
                        {audience.finished < audience.size &&
                            audience.type !== AudienceType.None && (
                                <span className={b('ordered-answers')}>
                                    {i18n.answers.ordered.replace(
                                        '%s',
                                        formatNumber(audience.size)
                                    )}
                                </span>
                            )}
                    </>
                )}
            </div>
            <div className={b('col', {type: 'cost'})}>
                {isFree
                    ? i18n.free
                    : formatPriceValue({value: priceWithoutVat || priceWithVat, currency})}
            </div>
            <div className={b('col', {type: 'targeting'})} title={targeting}>
                {targeting}
            </div>
            {listMode === ListMode.Agency && (
                <div className={b('col', {type: 'client'})}>{clientName}</div>
            )}
            <div className={b('col', {type: 'action'})}>
                <SurveyItemButton {...survey} />
            </div>
            <div className={b('col', {type: 'menu'})}>
                <SurveyItemMenu {...survey} />
            </div>
        </div>
    );
}

function SurveyRowSkeleton() {
    const listMode = useContext(ListModeContext);

    return (
        <div className={b('row', {type: 'dummy'})}>
            <div className={b('col', {type: 'name'})} />
            <div className={b('col', {type: 'date'})} />
            <div className={b('col', {type: 'state'})} />
            <div className={b('col', {type: 'answers'})} />
            <div className={b('col', {type: 'cost'})} />
            <div className={b('col', {type: 'targeting'})} />
            {listMode === ListMode.Agency && <div className={b('col', {type: 'client'})} />}
            <div className={b('col', {type: 'action'})} />
        </div>
    );
}

export default function Table(props: ListComponentProps) {
    const {list, segments, interests, categories, loadMore, isEntirelyLoaded} = props;

    const {height} = useWindowSize();
    const isSuper = useIsSuper();

    return (
        <InfiniteScroll
            className={b('items', {type: 'list'})}
            hasMore={!isEntirelyLoaded}
            loader={<RowLoader key='row-loader' />}
            loadMore={loadMore}
            threshold={height}
        >
            <HeaderRow />
            {list.map((item, index) => (
                <SurveyRow
                    categories={categories}
                    interests={interests}
                    key={isSurveyItem(item) ? item.slug : index}
                    segments={segments}
                    survey={item}
                    isSuper={isSuper}
                />
            ))}
        </InfiniteScroll>
    );
}
